/* eslint-disable no-param-reassign */
import { PathFormFieldProps } from 'interfaces/PathFormComponents.interface'
import {
  ReactElement,
  ChangeEvent,
  useRef,
  useEffect,
  useCallback,
  cloneElement,
  memo
} from 'react'

const DEFAULT_DELAY_IN_MS = 200

export interface OnChangeConstraints {
  delayInMs?: number
  minLength?: number
  doNotInvokeBlurOnChange?: boolean
}

interface OwnProps<T> {
  children: ReactElement<
    Omit<PathFormFieldProps<T>, 'onChange'> & {
      onChange: (value: T, event: ChangeEvent<HTMLElement>) => void
    }
  >
}

type Props<T> = OwnProps<T> & OnChangeConstraints

function isChangeEvent(value: unknown): value is ChangeEvent<HTMLInputElement> {
  return (
    (value as ChangeEvent<HTMLInputElement>)?.currentTarget?.value !== undefined
  )
}

function WithIdleFieldChange<T>({
  children,
  delayInMs = DEFAULT_DELAY_IN_MS
}: Props<T>) {
  const timeoutRef = useRef<number | null>(null)
  const valueRef = useRef<T | undefined>(undefined)

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    },
    []
  )

  const { onChange, ...restProps } = children.props

  const handleIdleChange = useCallback(
    (event: ChangeEvent<HTMLElement>) => {
      onChange(valueRef.current as T, event)
      timeoutRef.current = null
    },
    [onChange]
  )

  const changeHandler = useCallback(
    (value: T, event: ChangeEvent<HTMLElement>) => {
      valueRef.current = isChangeEvent(value)
        ? (value.currentTarget.value as unknown as T)
        : value

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      timeoutRef.current = window.setTimeout(
        () => handleIdleChange(event),
        delayInMs
      )
    },
    [delayInMs, handleIdleChange]
  )

  return cloneElement(children, {
    ...restProps,
    ...(Boolean(onChange) && {
      onChange: changeHandler
    })
  })
}

export default memo(WithIdleFieldChange) as typeof WithIdleFieldChange
